// Pakowanie: zestaw rozbity na składniki + komponenty (taśma, śrubka) — 2026-10-01, właściciel: „zestawy
// w zamówieniu mają być rozbijane, żeby pakowacz widział, co ma do zestawu spakować” oraz komponenty
// przypisane do produktu, do odhaczenia przy pakowaniu (nie ma czego skanować — potwierdza klik).
// Dane z backendu: `zestaw` (składniki karty-zestawu: id, name, sku, ean, eany_dodatkowe, location, image_url,
// per_unit, komponenty) i `komponenty` (id, name, description, image_url, per_unit) przy produkcie pozycji.

export type PozycjaPakowania = Record<string, any>;

/** Wspólne pola pozycji z oferty marketplace (tytuł i link) — kody oferty celowo POMIJAMY: skan kodu
 *  oferty zestawu nie może „potwierdzać” jego składnika ani komponentu. */
export type KontekstOferty = { offerName: string; offerUrl: string };

function _n(v: any, dom: number): number {
  const x = parseInt(String(v == null ? '' : v), 10);
  return isNaN(x) || x < 1 ? dom : x;
}

/** Produkt złożony z komponentów (SKU „…-SET”, np. ELE400003-R-SET = 2 puszki) zgłasza braki tylko komponentami —
 *  29.10.2026, właściciel: „przy zgłaszaniu braków produktu, który składa się z komponentu, też powinny być dostępne
 *  tylko komponenty, a nie cały zestaw”. Bez „-SET” komponent jest dodatkiem (dynie + świeczka LED) i produkt zostaje. */
export function zglaszanyKomponentami(sku: any): boolean {
  return /-SET$/i.test(String(sku || '').trim());
}

/** Komponenty (bez SKU/EAN) jako pozycje do odhaczenia: `ile` = sztuki produktu, do którego należą.
 *  `rodzicSku` — SKU produktu-rodzica (zglaszanyKomponentami). */
export function pozycjeKomponentow(komponenty: any, ile: number, klucz: string, dla: string,
                                   oferta: KontekstOferty, rodzicSku?: string): PozycjaPakowania[] {
  if (!Array.isArray(komponenty)) return [];
  const tylkoKomponenty = zglaszanyKomponentami(rodzicSku);
  return komponenty.filter((k: any) => k && String(k.name || '').trim()).map((k: any, i: number) => ({
    key: klucz + '_k' + i,
    // S-138: komponent to karta katalogu — skaner dopasuje jej SKU/EAN (brak kodów = klik jak dotąd)
    pid: k.id ? Number(k.id) : null,
    komponent: true,
    // 2026-10-01 (właściciel: „komponenty mają być widoczne jako część tego, co ktoś zamówił”) — pozycja-rodzic
    parentKey: klucz,
    dla: String(dla || ''),
    tylkoKomponenty: tylkoKomponenty,
    name: String(k.name || ''),
    opis: String(k.description || ''),
    sku: String(k.sku || ''), ean: String(k.ean || ''), eany: Array.isArray(k.eany_dodatkowe) ? k.eany_dodatkowe.map(String) : [],
    qty: Math.max(1, ile) * _n(k.per_unit, 1),
    // lokalizacja karty, z której powstał komponent (produkt jako komponent) — podpowiedź dla zbierającego
    location: String(k.location || ''),
    // id karty komponentu — zgłoszenie braku komponentu (S-138: = pid)
    zrodloPid: k.source_product_id || k.id || null,
    imgs: k.image_url ? [String(k.image_url)] : [],
    imgIdx: 0, confirmed: false,
    offerName: oferta.offerName, offerUrl: oferta.offerUrl, offerSku: '', offerEan: '',
  }));
}

/** Składniki zestawu (każdy z własnym SKU/EAN/lokalizacją, skan potwierdza) + ich komponenty.
 *  `ileZestawow` = sztuki zestawu (ilość z zamówienia × krotność powiązania). Pusty skład = []. */
export function pozycjeZestawu(zestaw: any, ileZestawow: number, klucz: string, nazwaZestawu: string,
                               oferta: KontekstOferty, zdjecia: (url: string) => string[]): PozycjaPakowania[] {
  if (!Array.isArray(zestaw) || !zestaw.length) return [];
  const out: PozycjaPakowania[] = [];
  zestaw.forEach((c: any, i: number) => {
    const ile = Math.max(1, ileZestawow) * _n(c && c.per_unit, 1);
    const k = klucz + '_z' + i;
    out.push({
      key: k,
      pid: (c && c.id) || null,
      zestaw: String(nazwaZestawu || ''),
      name: String((c && c.name) || ''),
      sku: String((c && c.sku) || ''),
      ean: String((c && c.ean) || ''),
      eany: (Array.isArray(c && c.eany_dodatkowe) ? c.eany_dodatkowe : []).map((x: any) => String(x || '').trim()).filter(Boolean),
      qty: ile,
      location: String((c && c.location) || ''),
      imgs: zdjecia(String((c && c.image_url) || '')),
      imgIdx: 0, confirmed: false,
      offerName: oferta.offerName, offerUrl: oferta.offerUrl, offerSku: '', offerEan: '',
    });
    pozycjeKomponentow(c && c.komponenty, ile, k, String((c && c.name) || ''), oferta, String((c && c.sku) || '')).forEach((p) => out.push(p));
  });
  return out;
}

export type DaneBraku = { productId: string; sku: string; name: string; qty: number; img: string; note: string;
                          zestawName?: string };

/** Dane przycisku „Zgłoś brak” dla pozycji pakowania (2026-10-01, właściciel: „nie można zgłosić braku komponentu,
 *  tylko cały produkt”). Komponent: karta, z której powstał (o ile jest), jego SKU i zdjęcie, ilość do dołożenia;
 *  `note` mówi w oknie zgłoszenia, do czego należy pozycja. */
export function daneBraku(p: PozycjaPakowania): DaneBraku {
  const pid = p && (p.komponent ? p.zrodloPid : p.pid);
  const note = !p ? '' : p.komponent
    ? 'Komponent' + (p.dla ? ' do: ' + String(p.dla) : '')
    : (p.zestaw ? 'Składnik zestawu: ' + String(p.zestaw) : '');
  return {
    productId: pid ? String(pid) : '',
    sku: String((p && (p.sku || p.offerSku)) || ''),
    name: String((p && p.name) || ''),
    qty: _n(p && p.qty, 1),
    img: String((p && Array.isArray(p.imgs) && p.imgs[0]) || ''),
    note: note,
    // 27.10.2026: składnik zestawu niesie nazwę zestawu — okno zgłoszenia pomija wtedy sam zestaw
    ...(p && !p.komponent && p.zestaw ? { zestawName: String(p.zestaw) } : {}),
    // 29.10.2026: komponent produktu „…-SET” — okno pomija sam produkt (zglaszanyKomponentami)
    ...(p && p.komponent && p.tylkoKomponenty && p.dla ? { zestawName: String(p.dla) } : {}),
  };
}

export type WpisSkladu = { name: string; zestaw: any[]; komponenty: any[] };
export type MapaSkladu = Record<string, WpisSkladu>;

/** {id karty: {name, zestaw, komponenty}} z pozycji zamówienia (pola doklejone przez backend), razem ze
 *  składnikami zestawów — ich komponenty też są do odhaczenia. */
export function mapaSkladuZPozycji(items: any): MapaSkladu {
  const out: MapaSkladu = {};
  const dodaj = (id: any, name: any, zestaw: any, komponenty: any): void => {
    if (id == null || id === '') return;
    const z = Array.isArray(zestaw) ? zestaw : [];
    const k = Array.isArray(komponenty) ? komponenty : [];
    const klucz = String(id);
    if (!out[klucz] || (!out[klucz].zestaw.length && z.length) || (!out[klucz].komponenty.length && k.length)) {
      out[klucz] = { name: String(name || ''), zestaw: z.length ? z : (out[klucz] ? out[klucz].zestaw : []),
                     komponenty: k.length ? k : (out[klucz] ? out[klucz].komponenty : []) };
    }
    z.forEach((c: any) => dodaj(c && c.id, c && c.name, [], c && c.komponenty));
  };
  (Array.isArray(items) ? items : []).forEach((it: any) => {
    if (!it) return;
    (Array.isArray(it.sellbase_products) ? it.sellbase_products : []).forEach((p: any) => dodaj(p && p.id, p && p.name, p && p.zestaw, p && p.komponenty));
    dodaj(it.sellbase_product_id, it.sellbase_product_name, it.sellbase_zestaw, it.sellbase_komponenty);
  });
  return out;
}

/** Pozycje z zamrożonego składu zamówienia (catalog-order) + komponenty (karty, składnika i samego zestawu).
 *  Wyjątek od „nie rozwijamy historii dzisiejszym składem”: karta sprzedana jako POJEDYNCZY produkt, która DZIŚ
 *  jest zestawem (np. #118815 — zestaw zdefiniowany po wpłynięciu zamówienia) — pakowacz pakuje fizyczne składniki,
 *  więc pokazujemy dzisiejszy skład z adnotacją `skladDzis`. Rozchód magazynu (snapshot) zostaje bez zmian.
 *  Pozycja katalogu niesie `soldKind` / `soldId` / `soldUnits` / `soldKey` (grupa sprzedanej karty). */
export function uzupelnijPozycjeKatalogu(pozycje: PozycjaPakowania[], mapa: MapaSkladu,
                                         zdjecia: (url: string) => string[]): PozycjaPakowania[] {
  const out: PozycjaPakowania[] = [];
  const lista = pozycje || [];
  lista.forEach((p, i) => {
    const wpis = mapa[String(p.pid)];
    const oferta = { offerName: String(p.offerName || ''), offerUrl: String(p.offerUrl || '') };
    if (wpis && wpis.zestaw.length && p.soldKind === 'product') {
      pozycjeZestawu(wpis.zestaw, p.qty, String(p.key), wpis.name || String(p.name || ''), oferta, zdjecia)
        .forEach((z) => out.push(z.komponent ? z : { ...z, skladDzis: true }));
      pozycjeKomponentow(wpis.komponenty, p.qty, String(p.key), wpis.name || String(p.name || ''), oferta, String(p.sku || '')).forEach((k) => out.push(k));
      return;
    }
    out.push(p);
    if (wpis) pozycjeKomponentow(wpis.komponenty, p.qty, String(p.key), String(p.name || ''), oferta, String(p.sku || '')).forEach((k) => out.push(k));
    // komponenty samego zestawu — po ostatnim składniku jego grupy
    const nast = lista[i + 1];
    if (p.soldKind === 'bundle' && p.soldId != null && (!nast || nast.soldKey !== p.soldKey)) {
      const wz = mapa[String(p.soldId)];
      if (wz) pozycjeKomponentow(wz.komponenty, _n(p.soldUnits, 1), 'zest-' + String(p.soldKey), wz.name, oferta).forEach((k) => out.push(k));
    }
  });
  return out;
}

export type GrupaPakowania = { glowna: PozycjaPakowania | null; dzieci: PozycjaPakowania[]; tytul: string; klucz: string };

/** Pozycje w grupach: produkt (albo składnik zestawu) + jego komponenty — pakowacz widzi komponenty jako część
 *  zamówionego produktu, nie osobne pozycje. Komponent bez pozycji-rodzica (np. komponenty samego zestawu) tworzy
 *  grupę „Włóż do paczki: <nazwa>”. Kolejność jak w liście pozycji. */
export function grupujPozycje(pozycje: PozycjaPakowania[]): GrupaPakowania[] {
  const grupy: GrupaPakowania[] = [];
  const poKluczu = new Map<string, GrupaPakowania>();
  (pozycje || []).forEach((p) => {
    if (p && p.komponent) {
      const rodzic = String(p.parentKey || '');
      let g = poKluczu.get(rodzic);
      if (!g) {
        g = { glowna: null, dzieci: [], tytul: String(p.dla || ''), klucz: 'bez-rodzica:' + rodzic };
        poKluczu.set(rodzic, g);
        grupy.push(g);
      }
      g.dzieci.push(p);
      return;
    }
    const g: GrupaPakowania = { glowna: p, dzieci: [], tytul: String((p && p.name) || ''), klucz: String(p && p.key) };
    poKluczu.set(String(p && p.key), g);
    grupy.push(g);
  });
  return grupy;
}

/** Grupa spakowana = produkt i wszystkie jego komponenty potwierdzone. */
export function grupaGotowa(g: GrupaPakowania): boolean {
  return (!g.glowna || !!g.glowna.confirmed) && g.dzieci.every((d) => !!d.confirmed);
}

/** Licznik „X z Y spakowanych” po zamówionych produktach (grupach), nie po każdym komponencie z osobna. */
export function licznikGrup(grupy: GrupaPakowania[]): { gotowe: number; wszystkie: number } {
  return { gotowe: (grupy || []).filter(grupaGotowa).length, wszystkie: (grupy || []).length };
}
